// 系统 → 数据清零 (testing-stage data reset).
//
// Clear inventory and/or task data — optionally scoped to specific dates via a
// multi-select calendar (none selected = all dates). Every clear is backed up
// server-side (rows are moved out, not destroyed) and restorable from the
// 恢复记录 tab. Guarded by 店长以上 + a password (vava123).

import { useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api.ts';
import { isElevated } from '../lib/auth.ts';
import { Toast } from '../components/atoms.jsx';
import { Icon } from '../components/Icon.jsx';
import { torontoToday, torontoMidnightUtcIso, torontoEndExclusiveUtcIso } from '../lib/torontoTime.js';

const SCOPES = [
  { key: 'inventory',      label: '库存记录', hint: '生产 / 售出 / 报废 / 调拨 / 改写（不含备货）' },
  { key: 'tasks',          label: '待办任务', hint: '任务 + 负责人 + 评论 + 依赖' },
  { key: 'prep',           label: '备货',     hint: '现货计划 + 录入完成 + 认证 + 下发的库存 · 全部门店' },
  { key: 'orders_summary', label: '订单汇总', hint: '订单 + 后厨汇总（按取货日）' }
];
// Store chips for the 库存 location scope — same vocabulary as 统计报表's filter.
// 'CK' = both kitchens (CK_RH/CK_NY). Empty selection = 全部门店 (no filter).
const STORES = [
  { key: 'RH', label: 'RH' },
  { key: 'DT', label: 'DT' },
  { key: 'NY', label: 'NY' },
  { key: 'CK', label: 'CK 中央厨房' }
];
const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// ---- multi-select month calendar -------------------------------------------
function MiniCalendar({ selected, onToggle }) {
  const today = torontoToday();                       // 'YYYY-MM-DD' ET
  const [view, setView] = useState(() => {
    const [y, m] = today.split('-').map(Number);
    return { y, m: m - 1 };                            // m: 0-based
  });
  const firstDow = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  function shift(delta) {
    setView(v => {
      const nm = v.m + delta;
      return { y: v.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
    });
  }

  return (
    <div className="dr-cal">
      <div className="dr-cal-head">
        <button type="button" onClick={() => shift(-1)} aria-label="上个月">‹</button>
        <span className="dr-cal-title">{view.y}年{view.m + 1}月</span>
        <button type="button" onClick={() => shift(1)} aria-label="下个月">›</button>
      </div>
      <div className="dr-cal-grid">
        {WEEKDAYS.map(w => <div key={w} className="dr-cal-wd">{w}</div>)}
        {cells.map((d, i) => {
          if (d === null) return <div key={`b${i}`} className="dr-cal-cell empty" />;
          const key = ymd(view.y, view.m, d);
          const future = key > today;
          const sel = selected.has(key);
          const isToday = key === today;
          const label = `${view.y}年${view.m + 1}月${d}日${isToday ? '（今天）' : ''}`;
          return (
            <button key={key} type="button"
                    className={`dr-cal-cell ${sel ? 'sel' : ''} ${isToday ? 'today' : ''}`}
                    disabled={future}
                    aria-pressed={sel}
                    aria-label={label}
                    onClick={() => onToggle(key)}>
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Skeleton rows matching a 5-column admin table, so the loading state reads as
// "a table is coming" instead of a bare "加载中…" line (consistent with the
// inventory grid + other lists in the app).
function SkelRows({ widths, rows = 3 }) {
  return Array.from({ length: rows }).map((_, r) => (
    <tr key={r}>
      {widths.map((w, i) => (
        <td key={i}><span className="skeleton dr-skel" style={{ width: w }} /></td>
      ))}
    </tr>
  ));
}

export const AdminDataReset = ({ user }) => {
  if (!isElevated(user)) {
    return (
      <div className="vava-card empty-state">
        <div className="emoji">🔒</div>
        <div className="title">无权访问</div>
        <div className="sub">数据清零仅限管理员 / 店长</div>
      </div>
    );
  }

  const [tab, setTab] = useState('clear');
  const [scopes, setScopes] = useState(new Set(['inventory']));
  const [locs, setLocs] = useState(new Set());           // store keys; empty = 全部门店
  const [selected, setSelected] = useState(new Set());   // ET date strings
  const [password, setPassword] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [batches, setBatches] = useState(null);
  const [backups, setBackups] = useState(null);

  const sortedDates = useMemo(() => [...selected].sort(), [selected]);

  function toggleScope(k) {
    setScopes(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  }
  // Store chips: 全部门店 = empty set. Toggling a store; selecting all four
  // collapses back to empty (= 全部, no filter) so the two are never ambiguous.
  function toggleLoc(k) {
    setLocs(prev => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      if (n.size === STORES.length) return new Set();   // all selected = 全部
      return n;
    });
  }
  const locSummary = locs.size ? [...locs].join('/') : '全部门店';
  function toggleDate(key) {
    setSelected(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }

  async function loadBatches() {
    try { setBatches(await api.listResetBatches()); }
    catch (e) { setToast({ kind: 'error', msg: e?.message || '加载失败' }); }
  }
  async function loadBackups() {
    try { setBackups(await api.listWeeklyBackups()); }
    catch (e) { setToast({ kind: 'error', msg: e?.message || '加载失败' }); }
  }
  useEffect(() => {
    if (tab === 'restore') loadBatches();
    if (tab === 'backup') loadBackups();
  }, [tab]);

  async function doBackupNow() {
    setBusy(true);
    try {
      const res = await api.runWeeklyBackup();
      setToast({ kind: 'success', msg: `已备份本周 ${res.count} 条库存记录` });
      await loadBackups();
    } catch (e) {
      setToast({ kind: 'error', msg: e?.message || '备份失败' });
    } finally { setBusy(false); }
  }

  const KIND_LABEL = { produced: '生产', sold: '售出', wasted: '报废', transferred: '调拨', adjusted: '改写', received: '收货' };
  async function downloadXlsx(b) {
    try {
      const full = await api.getWeeklyBackup(b.id);
      const rows = full.payload?.inventory || [];
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('库存记录');
      ws.columns = [
        { header: '日期时间', key: 'occurred_at', width: 20 },
        { header: '类型', key: 'kind', width: 10 },
        { header: '产品', key: 'product_name', width: 26 },
        { header: '地点', key: 'location', width: 10 },
        { header: '数量', key: 'qty', width: 10 },
        { header: '原因', key: 'reason', width: 12 },
        { header: '操作人', key: 'actor_id', width: 12 }
      ];
      ws.getRow(1).font = { bold: true };
      for (const r of rows) ws.addRow({ ...r, kind: KIND_LABEL[r.kind] || r.kind });
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `VAVA库存备份_${b.weekStart}_${b.weekEnd}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setToast({ kind: 'error', msg: e?.message || '导出失败' });
    }
  }

  async function doClear() {
    setBusy(true);
    try {
      const dates = sortedDates;
      const windows = dates.map(d => ({ from: torontoMidnightUtcIso(d), to: torontoEndExclusiveUtcIso(d) }));
      // location applies to 库存 only; send it only when a store subset is chosen.
      const sendLocs = scopes.has('inventory') && locs.size > 0;
      const res = await api.clearData({
        password, scopes: [...scopes],
        ...(dates.length ? { dates, windows } : {}),
        ...(sendLocs ? { locations: [...locs] } : {})
      });
      setToast({ kind: 'success', msg: `已清零 ${res.total} 条记录（可在恢复记录里找回）` });
      setConfirming(false); setPassword(''); setSelected(new Set()); setLocs(new Set());
    } catch (e) {
      setToast({ kind: 'error', msg: e?.message || '清零失败（密码错误？）' });
    } finally { setBusy(false); }
  }

  async function doRestore(id) {
    setBusy(true);
    try {
      const res = await api.restoreResetBatch(id);
      setToast({ kind: 'success', msg: `已恢复 ${res.restored} 条记录` });
      await loadBatches();
    } catch (e) {
      setToast({ kind: 'error', msg: e?.message || '恢复失败' });
    } finally { setBusy(false); }
  }

  const canClear = scopes.size > 0 && password.length > 0 && !busy;

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">数据清零</h1>
          <div className="vava-page-meta">测试阶段工具 · 清零会备份，可随时恢复</div>
        </div>
        <div className="vava-segmented" role="tablist">
          <button role="tab" id="drtab-clear" aria-controls="dr-panel" aria-selected={tab === 'clear'}
                  className={tab === 'clear' ? 'active' : ''} onClick={() => setTab('clear')}>清零</button>
          <button role="tab" id="drtab-restore" aria-controls="dr-panel" aria-selected={tab === 'restore'}
                  className={tab === 'restore' ? 'active' : ''} onClick={() => setTab('restore')}>恢复记录</button>
          <button role="tab" id="drtab-backup" aria-controls="dr-panel" aria-selected={tab === 'backup'}
                  className={tab === 'backup' ? 'active' : ''} onClick={() => setTab('backup')}>每周备份</button>
        </div>
      </div>

      <div id="dr-panel" role="tabpanel" aria-labelledby={`drtab-${tab}`}>
      {tab === 'clear' ? (
        <div className="vava-card dr-clear">
          <div className="att-warning" role="alert" style={{ marginBottom: 18 }}>
            <strong>清零会移除所选数据</strong> · 但系统会完整备份，可在「恢复记录」里一键找回。
          </div>
          <div className="dr-muted" style={{ marginBottom: 18, marginLeft: 0 }}>
            库存统计（今日 / 7天 / 30天）实时计算，清零后会同步归零。不选日期 = 清全部历史；
            只选某几天则只清那几天、滚动统计仍含其它日期。
          </div>

          <div className="dr-section">
            <div className="dr-section-title">1 · 选择范围</div>
            <div className="dr-scopes">
              {SCOPES.map(s => (
                <label key={s.key} className={`dr-scope ${scopes.has(s.key) ? 'on' : ''}`}>
                  <input type="checkbox" checked={scopes.has(s.key)} onChange={() => toggleScope(s.key)} />
                  <span className="dr-scope-label">{s.label}</span>
                  <span className="dr-scope-hint">{s.hint}</span>
                </label>
              ))}
            </div>
          </div>

          {scopes.has('inventory') && (
            <div className="dr-section">
              <div className="dr-section-title">
                2 · 选择门店 <span className="dr-muted">不选 = 全部门店 · 仅作用于库存记录</span>
              </div>
              <div className="dr-locs">
                <button type="button"
                        className={`dr-loc ${locs.size === 0 ? 'on' : ''}`}
                        aria-pressed={locs.size === 0}
                        onClick={() => setLocs(new Set())}>
                  全部门店
                </button>
                {STORES.map(s => (
                  <button key={s.key} type="button"
                          className={`dr-loc ${locs.has(s.key) ? 'on' : ''}`}
                          aria-pressed={locs.has(s.key)}
                          onClick={() => toggleLoc(s.key)}>
                    {s.label}
                  </button>
                ))}
              </div>
              {scopes.has('tasks') && locs.size > 0 && (
                <div className="dr-muted" style={{ marginTop: 8 }}>
                  门店仅作用于库存记录；待办任务按日期<strong>全部</strong>清零。
                </div>
              )}
            </div>
          )}

          <div className="dr-section">
            <div className="dr-section-title">
              3 · 选择日期 <span className="dr-muted">不选 = 全部日期</span>
            </div>
            <MiniCalendar selected={selected} onToggle={toggleDate} />
            <div className="dr-selected-summary">
              {sortedDates.length === 0
                ? <span className="dr-muted">未选日期 → 将清零<strong>全部</strong>历史数据</span>
                : <>已选 <strong>{sortedDates.length}</strong> 天：{sortedDates.join('、')}
                    <button type="button" className="inv-link-btn" onClick={() => setSelected(new Set())}>清除选择</button></>}
            </div>
          </div>

          <div className="dr-section">
            <div className="dr-section-title">4 · 输入密码</div>
            <input className="vava-input dr-pw" type="password" autoComplete="off"
                   aria-label="清零密码" placeholder="清零密码" value={password}
                   onChange={(e) => setPassword(e.target.value)} />
          </div>

          {!confirming ? (
            <button className="btn dr-danger" disabled={!canClear} onClick={() => setConfirming(true)}>
              <Icon name="trash" /> 一键清零
            </button>
          ) : (
            <div className="dr-confirm">
              <span>确定清零 <strong>{[...scopes].map(k => SCOPES.find(s => s.key === k).label).join(' + ')}</strong>
                {scopes.has('inventory') && <> · 门店 <strong>{locSummary}</strong></>}
                {' · '}{sortedDates.length ? `${sortedDates.length} 天` : '全部日期'}？</span>
              <div className="dr-confirm-actions">
                <button className="btn btn-ghost" onClick={() => setConfirming(false)} disabled={busy}>取消</button>
                <button className="btn dr-danger" onClick={doClear} disabled={busy}>{busy ? '清零中…' : '确定清零'}</button>
              </div>
            </div>
          )}
        </div>
      ) : tab === 'restore' ? (
        <div className="vava-card">
          {batches === null ? (
            <div className="vava-table-scroll">
            <table className="vava-table" aria-busy="true">
              <thead>
                <tr><th>范围</th><th style={{ width: 80, textAlign: 'right' }}>条数</th><th style={{ width: 120 }}>操作人</th><th style={{ width: 160 }}>时间</th><th style={{ width: 110 }}></th></tr>
              </thead>
              <tbody><SkelRows widths={['70%', '30%', '55%', '65%', '45%']} /></tbody>
            </table>
            </div>
          ) : batches.length === 0 ? (
            <div className="empty-state"><div className="emoji">🗂</div><div className="title">还没有清零记录</div></div>
          ) : (
            <div className="vava-table-scroll">
            <table className="vava-table">
              <thead>
                <tr><th>范围</th><th style={{ width: 80, textAlign: 'right' }}>条数</th><th style={{ width: 120 }}>操作人</th><th style={{ width: 160 }}>时间</th><th style={{ width: 110 }}></th></tr>
              </thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.id}>
                    <td>{b.scopeLabel}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{b.total}</td>
                    <td>{b.actorName || '—'}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{(b.createdAt || '').replace('T', ' ').slice(0, 16)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {b.restoredAt
                        ? <span className="status status-已完成"><span className="dot" />已恢复</span>
                        : <button className="btn btn-secondary" disabled={busy} onClick={() => doRestore(b.id)}>恢复</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      ) : (
        <div className="vava-card">
          <div className="dr-backup-head">
            <div className="dr-muted" style={{ margin: 0 }}>每周一自动备份上一周的库存记录 · 也可手动备份本周。</div>
            <button className="btn btn-secondary" disabled={busy} onClick={doBackupNow}>
              <Icon name="reset" /> 立即备份本周
            </button>
          </div>
          {backups === null ? (
            <div className="vava-table-scroll">
            <table className="vava-table" aria-busy="true">
              <thead>
                <tr><th>周</th><th style={{ width: 70, textAlign: 'right' }}>条数</th><th style={{ width: 70 }}>来源</th><th style={{ width: 150 }}>生成时间</th><th style={{ width: 120 }}></th></tr>
              </thead>
              <tbody><SkelRows widths={['80%', '30%', '40%', '65%', '50%']} /></tbody>
            </table>
            </div>
          ) : backups.length === 0 ? (
            <div className="empty-state"><div className="emoji">🗂</div><div className="title">还没有每周备份</div><div className="sub">点「立即备份本周」生成第一份</div></div>
          ) : (
            <div className="vava-table-scroll">
            <table className="vava-table">
              <thead>
                <tr><th>周</th><th style={{ width: 70, textAlign: 'right' }}>条数</th><th style={{ width: 70 }}>来源</th><th style={{ width: 150 }}>生成时间</th><th style={{ width: 120 }}></th></tr>
              </thead>
              <tbody>
                {backups.map(b => (
                  <tr key={b.id}>
                    <td className="mono">{b.weekStart} ~ {b.weekEnd}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{b.total}</td>
                    <td><span className="cat-tag">{b.kind === 'auto' ? '自动' : '手动'}</span></td>
                    <td className="mono" style={{ fontSize: 12 }}>{(b.createdAt || '').replace('T', ' ').slice(0, 16)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-secondary" disabled={busy || b.total === 0} onClick={() => downloadXlsx(b)}>下载 Excel</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
      </div>

      {toast && <Toast message={toast.msg} kind={toast.kind} onDone={() => setToast(null)} />}
    </>
  );
};
